import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, TextField, Button, MenuItem, Select,
  FormControl, InputLabel, Switch, FormControlLabel, IconButton, Divider,
  Accordion, AccordionSummary, AccordionDetails, Tabs, Tab, CircularProgress, Alert,
  Autocomplete
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import axios from 'axios';
import { useCompetitions } from '../CompetitionContext';

const LeagueManager = () => {
  const { competitions } = useCompetitions();
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [leagueDetails, setLeagueDetails] = useState(null);
  const [metadata, setMetadata] = useState({ regions: [], districts: [] });
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  // Przygotowanie listy wszystkich lig do Autocomplete
  const allLeaguesList = Object.values(competitions).flat()
    .sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));

  // Pobieranie danych ligi
  useEffect(() => {
    if (!selectedLeagueId) return;
    
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/api/admin/league-manager/${selectedLeagueId}`)
      .then(res => {
        setLeagueDetails(res.data);
        if (res.data.metadata) {
          setMetadata(res.data.metadata);
        }
      })
      .catch(err => {
        console.error("Błąd ładowania danych ligi:", err);
      })
      .finally(() => setLoading(false));
  }, [selectedLeagueId]);

  const handleSave = async () => {
    if (!selectedLeagueId) return;
    setSaveLoading(true);
    
    // Debugowanie adresu i danych
    const url = `${process.env.REACT_APP_API_URL}/api/admin/league-manager/${selectedLeagueId}`;
    console.log("Wysyłanie PUT do:", url);
    console.log("Payload:", leagueDetails);

    try {
      const response = await axios.put(url, leagueDetails);
      alert("Wszystkie zmiany zostały zapisane!");
      console.log("Odpowiedź serwera:", response.data);
    } catch (err) {
      console.error("SZCZEGÓŁY BŁĘDU ZAPISU:", err);
      if (err.response) {
        alert(`Błąd serwera: ${err.response.status} - ${JSON.stringify(err.response.data)}`);
      } else if (err.request) {
        alert("Błąd sieciowy (Network Error): Serwer nie odpowiedział. Sprawdź czy backend działa i czy adres URL jest poprawny.");
      } else {
        alert("Błąd: " + err.message);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const updateField = (field, value) => {
    setLeagueDetails(prev => ({ ...prev, [field]: value }));
  };

  // --- Logika zarządzania strukturą baraży ---
  const addPlayoffTemplate = () => {
    const newTpl = { id: 0, name: "Nowy Szablon Barażowy", draw_required: true, rounds: [] };
    setLeagueDetails(prev => ({ ...prev, playoff_templates: [...(prev.playoff_templates || []), newTpl] }));
  };

  const addRound = (tIndex) => {
    const up = { ...leagueDetails };
    const rounds = up.playoff_templates[tIndex].rounds || [];
    rounds.push({ round_name: "Półfinał", order_index: rounds.length + 1, is_double_leg: false, participants: [] });
    up.playoff_templates[tIndex].rounds = rounds;
    setLeagueDetails(up);
  };

  const addParticipant = (tIndex, rIndex) => {
    const up = { ...leagueDetails };
    const parts = up.playoff_templates[tIndex].rounds[rIndex].participants || [];
    parts.push({ source_competition_id: selectedLeagueId, pos_start: 2, pos_end: 2, bracket_group: 1 });
    up.playoff_templates[tIndex].rounds[rIndex].participants = parts;
    setLeagueDetails(up);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>Zarządzanie Ligami</Typography>

      {/* AUTOCOMPLETE DO WYBORU LIGI */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#1e1e1e' }}>
        <Autocomplete
          options={allLeaguesList}
          getOptionLabel={(option) => `[Lvl ${option.level}] ${option.name} ${option.group || ''}`}
          value={allLeaguesList.find(l => l.competition_id === selectedLeagueId) || null}
          onChange={(event, newValue) => {
            setSelectedLeagueId(newValue ? newValue.competition_id : '');
          }}
          renderInput={(params) => (
            <TextField {...params} label="Wyszukaj ligę do edycji (wpisz nazwę...)" variant="outlined" />
          )}
          fullWidth
        />
      </Paper>

      {leagueDetails && (
        <Paper sx={{ p: 3, bgcolor: '#1e1e1e', minHeight: '600px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
                <Typography variant="h5" color="primary">{leagueDetails.name} {leagueDetails.group}</Typography>
                <Typography variant="caption" color="textSecondary">ID: {selectedLeagueId} | Sezon: {leagueDetails.season}</Typography>
            </Box>
            <Button 
              variant="contained" 
              color="success" 
              size="large" 
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleSave}
              disabled={saveLoading}
            >
              {saveLoading ? 'Zapisywanie...' : 'ZAPISZ WSZYSTKIE ZMIANY'}
            </Button>
          </Box>

          <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Parametry Podstawowe" />
            <Tab label="Awanse i Spadki" />
            <Tab label="Kreator Baraży" />
          </Tabs>

          {/* TAB 0: DANE PODSTAWOWE */}
          {tabIndex === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Oficjalna Nazwa" value={leagueDetails.name || ''} onChange={e => updateField('name', e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth label="Nazwa Grupy" value={leagueDetails.group || ''} onChange={e => updateField('group', e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth label="Sezon" value={leagueDetails.season || ''} onChange={e => updateField('season', e.target.value)} sx={{ mb: 2 }} />
                <TextField fullWidth label="Poziom Rozgrywkowy" type="number" value={leagueDetails.level || 0} onChange={e => updateField('level', parseInt(e.target.value))} />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Region (Województwo)</InputLabel>
                  <Select value={leagueDetails.region_id || 0} label="Region" onChange={e => updateField('region_id', e.target.value)}>
                    <MenuItem value={0}>Brak / Ogólnopolski</MenuItem>
                    {metadata.regions.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Okręg (District)</InputLabel>
                  <Select value={leagueDetails.district_id || 0} label="Okręg" onChange={e => updateField('district_id', e.target.value)}>
                    <MenuItem value={0}>Brak</MenuItem>
                    {metadata.districts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Liga wyżej (Awans do)</InputLabel>
                  <Select value={leagueDetails.promoted_competition_id || 0} label="Liga wyżej" onChange={e => updateField('promoted_competition_id', e.target.value)}>
                    <MenuItem value={0}>Brak (Szczyt hierarchii)</MenuItem>
                    {allLeaguesList.filter(l => l.level < leagueDetails.level).map(l => (
                      <MenuItem key={l.competition_id} value={l.competition_id}>{l.name} {l.group}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Dodatkowe Opcje</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                     <TextField fullWidth label="Link (90minut.pl / Transfermarkt)" value={leagueDetails.link || ''} onChange={e => updateField('link', e.target.value)} />
                  </Grid>
                  {/* Zmienione na pola tekstowe (TextField) */}
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Wymagania Statystyk (np. minuty)" value={leagueDetails.stats_requirement || ''} onChange={e => updateField('stats_requirement', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Wymagania Pełne (np. składy)" value={leagueDetails.full_requirement || ''} onChange={e => updateField('full_requirement', e.target.value)} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* TAB 1: AWANSE I SPADKI */}
          {tabIndex === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#252525', borderLeft: '4px solid #4caf50' }}>
                  <Typography variant="h6" color="success.main" gutterBottom>Strefa Awansu</Typography>
                  <TextField fullWidth label="Miejsca premiowane awansem" type="number" sx={{ mb: 2 }}
                    value={leagueDetails.promotion_spots || 0} onChange={e => updateField('promotion_spots', parseInt(e.target.value))} />
                  <TextField fullWidth label="Miejsca w barażach o awans" type="number"
                    value={leagueDetails.promotion_playoff_spots || 0} onChange={e => updateField('promotion_playoff_spots', parseInt(e.target.value))} />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#252525', borderLeft: '4px solid #f44336' }}>
                  <Typography variant="h6" color="error.main" gutterBottom>Strefa Spadku</Typography>
                  <TextField fullWidth label="Liczba spadkowiczów" type="number" sx={{ mb: 2 }}
                    value={leagueDetails.relegation_spots || 0} onChange={e => updateField('relegation_spots', parseInt(e.target.value))} />
                  <TextField fullWidth label="Baraże o utrzymanie" type="number"
                    value={leagueDetails.relegation_playoff_spots || 0} onChange={e => updateField('relegation_playoff_spots', parseInt(e.target.value))} />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#252525' }}>
                   <Typography variant="h6" gutterBottom>Ustawienia Kaskady</Typography>
                   <TextField fullWidth label="Docelowy rozmiar ligi" type="number" sx={{ mb: 2 }}
                      value={leagueDetails.target_size || 0} onChange={e => updateField('target_size', parseInt(e.target.value))} />
                   <FormControlLabel control={<Switch checked={leagueDetails.is_balancing_active || false} onChange={e => updateField('is_balancing_active', e.target.checked)} />} 
                      label="Aktywuj kaskadę (dynamiczne spadki)" />
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* TAB 2: KREATOR BARAŻY */}
          {tabIndex === 2 && (
            <Box>
              <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addPlayoffTemplate} sx={{ mb: 3 }}>Dodaj Baraż</Button>
              {leagueDetails.playoff_templates?.map((tpl, tIndex) => (
                <Accordion key={tIndex} sx={{ mb: 2, bgcolor: '#2a2a2a' }}>
                  <AccordionSummary expandMore={ExpandMoreIcon}>
                    <Typography sx={{ flexGrow: 1 }}>{tpl.name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField fullWidth label="Nazwa" size="small" value={tpl.name} onChange={e => {
                      const up = {...leagueDetails}; up.playoff_templates[tIndex].name = e.target.value; setLeagueDetails(up);
                    }} sx={{ mb: 2 }} />

                    {tpl.rounds?.map((rnd, rIndex) => (
                      <Paper key={rIndex} sx={{ p: 2, mb: 2, bgcolor: '#333' }}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                          <TextField label="Nazwa rundy" size="small" value={rnd.round_name} onChange={e => {
                            const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].round_name = e.target.value; setLeagueDetails(up);
                          }} />
                        </Box>
                        {rnd.participants?.map((part, pIndex) => (
                          <Box key={pIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Select size="small" value={part.source_competition_id} onChange={e => {
                              const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].participants[pIndex].source_competition_id = e.target.value; setLeagueDetails(up);
                            }}>
                              {allLeaguesList.map(l => <MenuItem key={l.competition_id} value={l.competition_id}>{l.name} {l.group}</MenuItem>)}
                            </Select>
                            <TextField label="Od" type="number" size="small" sx={{ width: 70 }} value={part.pos_start} onChange={e => {
                                const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].participants[pIndex].pos_start = parseInt(e.target.value); setLeagueDetails(up);
                            }} />
                            <TextField label="Do" type="number" size="small" sx={{ width: 70 }} value={part.pos_end} onChange={e => {
                                const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].participants[pIndex].pos_end = parseInt(e.target.value); setLeagueDetails(up);
                            }} />
                             <IconButton color="error" onClick={() => {
                               const up = {...leagueDetails}; up.playoff_templates[tIndex].rounds[rIndex].participants.splice(pIndex, 1); setLeagueDetails(up);
                            }}><DeleteIcon /></IconButton>
                          </Box>
                        ))}
                        <Button size="small" onClick={() => addParticipant(tIndex, rIndex)}>+ Dodaj Uczestników</Button>
                      </Paper>
                    ))}
                    <Button variant="outlined" size="small" onClick={() => addRound(tIndex)}>+ Dodaj Rundę</Button>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default LeagueManager;